import {
  date as commonDate,
  typeguards as commonTypeguards,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  AggregateBorrowingRiskInsightsGqlDataGQLType,
  AggregateReceivableRiskInsightsGqlDataGQLType,
  CreditFacilityGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { AggregateBorrowingRiskChartData } from '../shared/portfolioChartUtils';

export const portfolioRiskTableFacilityColumnId = 'creditFacilitySlug' as const;

export type PortfolioRiskTableRow = {
  creditFacilityName: string;
  creditFacilitySlug: string;
  [key: Util.FullDateStr]: number | null;
};

export type RiskFieldToTableRowMap = Record<
  | keyof Pick<
      AggregateBorrowingRiskInsightsGqlDataGQLType,
      'collateralCovenantLtv' | 'netOutstandingLtv' | 'performingLtv'
    >
  | keyof Pick<
      AggregateReceivableRiskInsightsGqlDataGQLType,
      | 'chargedOffPercentageOfTotalExposure'
      | 'delinquentPercentageOfTotalExposure'
    >,
  PortfolioRiskTableRow[]
>;

// CONSTS //

const stickyHeaderColumnWidth = '250px';
const stickyHeaderColumnZIndex = 2; // left-most column of table, frozen for horizontal scrolling

export const fixedColumnsStyleToColumnIndexMap = [
  // facility column, sticky
  {
    width: stickyHeaderColumnWidth,
    minWidth: stickyHeaderColumnWidth,
    maxWidth: stickyHeaderColumnWidth,
    left: 0,
    zIndex: stickyHeaderColumnZIndex,
    borderRightWidth: 'medium',
  },
];

// DATA FORMATTERS //

/**
 * Given the filtered chart data and a base daily set of input data
 * Return table row map of all fields for Risk tables
 * Where each property represents all the table row data for a single risk field (eg. net performing ltv)
 */
export const formatRiskTablesDataMap = ({
  chartDataWithDateRangeAndTimePeriod,
  dailyBorrowingBaseRiskData,
  dailyReceivableRiskData,
  filteredCreditFacilities,
}: {
  chartDataWithDateRangeAndTimePeriod: AggregateBorrowingRiskChartData[];
  dailyBorrowingBaseRiskData: AggregateBorrowingRiskInsightsGqlDataGQLType[];
  dailyReceivableRiskData: AggregateReceivableRiskInsightsGqlDataGQLType[];
  filteredCreditFacilities: Pick<
    CreditFacilityGQLType,
    'id' | 'name' | 'slug'
  >[];
}): RiskFieldToTableRowMap => {
  const requestedReportingDatesSet = new Set<Util.FullDateStr>();
  chartDataWithDateRangeAndTimePeriod.forEach(({ reportingDate }) =>
    requestedReportingDatesSet.add(reportingDate),
  );

  const riskDataMapByField: Record<
    | keyof Pick<
        AggregateBorrowingRiskInsightsGqlDataGQLType,
        'collateralCovenantLtv' | 'netOutstandingLtv' | 'performingLtv'
      >
    | keyof Pick<
        AggregateReceivableRiskInsightsGqlDataGQLType,
        | 'chargedOffPercentageOfTotalExposure'
        | 'delinquentPercentageOfTotalExposure'
      >,
    { [key: string]: PortfolioRiskTableRow }
  > = {
    collateralCovenantLtv: {},
    netOutstandingLtv: {},
    performingLtv: {},
    chargedOffPercentageOfTotalExposure: {},
    delinquentPercentageOfTotalExposure: {},
  };

  const borrowingBaseRiskKeys = [
    'collateralCovenantLtv',
    'netOutstandingLtv',
    'performingLtv',
  ] as const;
  const receivableRiskKeys = [
    'chargedOffPercentageOfTotalExposure',
    'delinquentPercentageOfTotalExposure',
  ] as const;

  dailyBorrowingBaseRiskData.forEach(({ creditFacilityData }) => {
    creditFacilityData.forEach((datapoint) => {
      const { reportingDate, creditFacilitySlug } = datapoint;

      const creditFacility = filteredCreditFacilities.find(
        (facility) => facility.slug === creditFacilitySlug,
      );
      if (requestedReportingDatesSet.has(reportingDate) && creditFacility) {
        borrowingBaseRiskKeys.forEach((key) => {
          if (!riskDataMapByField[key][creditFacilitySlug]) {
            riskDataMapByField[key][creditFacilitySlug] = {
              creditFacilitySlug: creditFacility.slug,
              creditFacilityName: creditFacility.name,
            };
          }
          riskDataMapByField[key][creditFacilitySlug][reportingDate] =
            datapoint[key];
        });
      }
    });
  });

  dailyReceivableRiskData.forEach(({ creditFacilityData }) => {
    creditFacilityData.forEach((datapoint) => {
      const { reportingDate, creditFacilitySlug } = datapoint;
      const creditFacility = filteredCreditFacilities.find(
        (facility) => facility.slug === creditFacilitySlug,
      );
      if (requestedReportingDatesSet.has(reportingDate) && creditFacility) {
        receivableRiskKeys.forEach((key) => {
          if (!riskDataMapByField[key][creditFacilitySlug]) {
            riskDataMapByField[key][creditFacilitySlug] = {
              creditFacilitySlug: creditFacility.slug,
              creditFacilityName: creditFacility.name,
            };
          }
          riskDataMapByField[key][creditFacilitySlug][reportingDate] =
            datapoint[key];
        });
      }
    });
  });

  const riskFieldToTableRowMap: RiskFieldToTableRowMap = {
    collateralCovenantLtv: Object.values(
      riskDataMapByField.collateralCovenantLtv,
    ),
    netOutstandingLtv: Object.values(riskDataMapByField.netOutstandingLtv),
    performingLtv: Object.values(riskDataMapByField.performingLtv),
    chargedOffPercentageOfTotalExposure: Object.values(
      riskDataMapByField.chargedOffPercentageOfTotalExposure,
    ),
    delinquentPercentageOfTotalExposure: Object.values(
      riskDataMapByField.delinquentPercentageOfTotalExposure,
    ),
  };

  return riskFieldToTableRowMap;
};

// REACT TABLE UTILS //

const formatColumnDef = (
  accessorKey: string | Util.FullDateStr,
  header?: string,
) => {
  return {
    accessorKey,
    header: header ?? accessorKey,
  };
};

/**
 * Given the filtered chart data with the requested reporting dates
 * Return an array of header keys for the Risk table
 */
const formatTableReportingDateHeaderKeys = ({
  chartDataWithDateRangeAndTimePeriod,
}: {
  chartDataWithDateRangeAndTimePeriod: AggregateBorrowingRiskChartData[];
}): string[] => {
  const requestedReportingDateHeaders = chartDataWithDateRangeAndTimePeriod.map(
    ({ reportingDate }) => reportingDate,
  );

  return [portfolioRiskTableFacilityColumnId, ...requestedReportingDateHeaders];
};

type ColumnDef = { accessorKey: string; header: string };
export const formatRiskTableColumns = (
  chartDataWithDateRangeAndTimePeriod: AggregateBorrowingRiskChartData[],
): ColumnDef[] => {
  const headerKeys = formatTableReportingDateHeaderKeys({
    chartDataWithDateRangeAndTimePeriod,
  });

  return headerKeys
    .map((headerKey): ColumnDef | undefined => {
      if (headerKey === portfolioRiskTableFacilityColumnId) {
        return formatColumnDef(headerKey, 'Facility');
      }
      if (commonTypeguards.isFullDateStr(headerKey)) {
        return formatColumnDef(
          headerKey,
          commonDate.fullDateStrToStr(headerKey, 'LLL d'),
        );
      }
      return undefined;
    })
    .filter((val): val is ColumnDef => !!val);
};
